import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row } from 'react-bootstrap';
import { Paging } from '../../Helpers/Paging';
import Loading from "../../Common/Loading";
import { Link, useLocation, useNavigate } from 'react-router-dom';

/**
 * VehicleGroup Component
 *
 * Manages the display and management of vehicles within a specific group.
 * Allows searching for vehicles by reference, displays vehicle lists with pagination,
 * and provides functionality to remove vehicles from the group.
 *
 * @param {Object} props - Component properties.
 * @param {string} props.userAccessToken - User's access token for API authorization.
 *
 * @returns {JSX.Element} The rendered component.
 */
const VehicleGroup = (props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const urlParam = location.search.substring(1);
    const groupName = decodeURIComponent(urlParam).replace(/%20/g, " ");

    const [VehicleGroupsData, setVehicleGroupsData] = useState([]);
    const [vehicleReference, setVehicleReference] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [numberOfPages, setNumberOfPages] = useState(1);

    /**
     * useEffect Hook
     *
     * Sets the document title and fetches vehicle groups when dependencies change.
     */
    useEffect(() => {
        document.title = "UBI Vehicle Group Management";
        getVehicleGroups(pageSize, pageNumber, groupName);
    }, [pageSize, pageNumber, groupName]);

    /**
     * Fetches vehicle groups from the API based on group name and vehicle reference with pagination.
     *
     * @async
     * @function getVehicleGroups
     * @param {number} pageSize - Number of vehicles per page.
     * @param {number} pageNumber - Current page number.
     * @param {string} groupName - Name of the group to fetch vehicles from.
     * @throws Will set error state if the fetch fails.
     */
    const getVehicleGroups = useCallback(async (pageSize, pageNumber, groupName) => {
        try {
            const response = await props.apiFetch(
                `/api/GroupManagement/GetVehicleGroups?GroupName=${encodeURIComponent(groupName)}&VehicleReference=${encodeURIComponent(vehicleReference)}&PagingRequest.PageSize=${pageSize}&PagingRequest.PageNumber=${pageNumber}`
            );
            if (!response.ok) {
                throw new Error("Failed to fetch vehicle groups");
            }
            const data = await response.json();
            setVehicleGroupsData(data.results);
            setLoading(false);
            setError(false);
            setTotalResults(data.pageDetail.totalResults);
            setNumberOfPages(data.pageDetail.numberOfPages);
            setPageNumber(data.pageDetail.pageNumber);
        } catch (error) {
            console.error(error);
            setVehicleGroupsData([]);
            setLoading(false);
            setError(true);
            setTotalResults(0);
            setNumberOfPages(1);
            setPageNumber(1);
        }
    }, [vehicleReference, props]);

    /**
     * Handles the search form submission to filter vehicles by reference.
     *
     * @function searchGroups
     * @param {Event} event - Form submission event.
     */
    const searchGroups = (event) => {
        event.preventDefault();
        setLoading(true);
        setTotalResults(0);
        setNumberOfPages(1);
        setPageNumber(1);
        getVehicleGroups(pageSize, 1, groupName);
    };

    /**
     * Clears the search input and reloads the vehicle group list.
     *
     * @function clearSearch
     */
    const clearSearch = () => {
        setVehicleReference('');
        setLoading(true);
        getVehicleGroups(pageSize, pageNumber, groupName);
    };

    /**
     * Updates the vehicle reference state based on user input.
     *
     * @function handleVehicleReferenceChange
     * @param {Event} event - Input change event.
     */
    const handleVehicleReferenceChange = (event) => {
        setVehicleReference(event.target.value);
    };

    /**
     * Handles the deletion of a vehicle from the group after confirmation.
     *
     * @async
     * @function handleDeleteVehicleGroup
     * @param {number} vehicleGroupId - ID of the vehicle-group association to delete.
     * @param {string} vehicleReference - Reference of the vehicle to remove.
     */
    const handleDeleteVehicleGroup = async (vehicleGroupId, vehicleReference) => {
        if (window.confirm(`Do you want to remove ${vehicleReference} from this group?`)) {
            const requestOptions = {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ vehicleGroupId })
            };
            try {
                const response = await props.apiFetch('/api/GroupManagement/DeleteVehicleGroup', requestOptions);
                if (!response.ok) {
                    throw new Error("Failed to delete vehicle group");
                }
                getVehicleGroups(pageSize, pageNumber, groupName);
            } catch (error) {
                console.error(error);
                // Optionally handle error display to the user here
            }
        }
    };

    let content = null;
    if (loading) {
        content = <Loading />;
    } else if (error) {
        content = <p>There was an error.</p>;
    } else if (VehicleGroupsData.length === 0) {
        content = <p>No vehicle groups found.</p>;
    } else {
        content = (
            <div className="content-vehicle-list">
                <table className="table table-striped table-dark">
                    <thead>
                    <tr>
                        <th>Vehicle Reference</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {VehicleGroupsData.map(vehicleGroup => (
                        <tr key={vehicleGroup.vehicleGroupId}>
                            <td>{vehicleGroup.vehicleReference}</td>
                            <td>
                                <Button
                                    variant="outline-danger"
                                    type="button"
                                    onClick={() => handleDeleteVehicleGroup(vehicleGroup.vehicleGroupId, vehicleGroup.vehicleReference)}>
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        );
    }

    return (
        <div className="webcontainer">
            <Container fluid>
                <Row>
                    <Col md={12} lg={12} className="fixed-col2">
                        <div className="contentvehicle3">
                            <h4>Vehicle Group Management: {groupName}</h4>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col md={12} lg={2}>
                        <div className="contentsearch mt-5">
                            <h5>Search</h5>
                            <Form onSubmit={searchGroups}>
                                <Form.Group controlId="formBasicEmail">
                                    <Form.Control
                                        type="text"
                                        value={vehicleReference}
                                        onChange={handleVehicleReferenceChange}
                                        placeholder="Enter a Vehicle"
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    onClick={searchGroups}
                                    className="mr-3">
                                    Search
                                </Button>
                                <Button
                                    variant="secondary"
                                    type="button"
                                    onClick={clearSearch}>
                                    Clear
                                </Button>
                            </Form>
                        </div>
                        <div className="myvehicles-button">
                            <Link to={`/admin/addvehiclegroup?${encodeURIComponent(groupName)}`}>
                                <Button variant="success" className="full-button">
                                    Add Vehicle to Group
                                </Button>
                            </Link>
                        </div>
                    </Col>
                    <Paging
                        content={content}
                        pageSize={pageSize}
                        pageNumber={pageNumber}
                        totalResults={totalResults}
                        numberOfPages={numberOfPages}
                        onChange={(newPage) => getVehicleGroups(pageSize, newPage, groupName)}
                        pageSizeChange={(newSize) => getVehicleGroups(newSize, 1, groupName)}
                    />
                </Row>
            </Container>
        </div>
    );
};

export default VehicleGroup;
